import { useState } from 'react';
import { motion } from 'framer-motion';
import { Calculator, CalendarCheck, BellRing, Gauge } from 'lucide-react';
import { Reveal, Eyebrow } from './Reveal';
import { ASSETS } from '../constants/site';

const SERVICES = { 'Business Website': 1800, 'Booking Platform': 2600, 'E-Commerce Store': 3400 };

function QuoteEstimator() {
  const [service, setService] = useState('Business Website');
  const [pages, setPages] = useState(6);
  const [addons, setAddons] = useState({ estimator: true, booking: false, portal: false });

  const base = SERVICES[service] + pages * 120
    + (addons.estimator ? 900 : 0) + (addons.booking ? 800 : 0) + (addons.portal ? 1500 : 0);
  const low = Math.round(base / 50) * 50;
  const high = Math.round((base * 1.2) / 50) * 50;

  return (
    <div className="flex flex-col h-full">
      <div className="flex flex-wrap gap-2 mb-5">
        {Object.keys(SERVICES).map((s) => (
          <button
            key={s}
            data-testid="quote-estimator-input"
            onClick={() => setService(s)}
            className={`text-xs font-mono-tech px-4 py-2 rounded-full border transition-colors duration-300 ${
              service === s ? 'border-amber-400 text-amber-300 bg-amber-500/10' : 'border-slate-700 text-slate-400 hover:border-slate-500'
            }`}
          >
            {s}
          </button>
        ))}
      </div>
      <label className="text-xs text-slate-400 font-mono-tech tracking-widest uppercase mb-2">
        Pages — <span className="text-amber-300">{pages}</span>
      </label>
      <input
        type="range" min="3" max="15" value={pages}
        onChange={(e) => setPages(Number(e.target.value))}
        className="w-full accent-amber-400 mb-5"
        data-testid="quote-estimator-pages-slider"
      />
      <div className="flex flex-wrap gap-2 mb-6">
        {[['estimator', 'Quote Estimator'], ['booking', 'Calendar Booking'], ['portal', 'Owner Portal']].map(([k, label]) => (
          <button
            key={k}
            onClick={() => setAddons({ ...addons, [k]: !addons[k] })}
            className={`text-xs px-4 py-2 rounded-full border transition-colors duration-300 ${
              addons[k] ? 'border-sky-400 text-sky-300 bg-sky-500/10' : 'border-slate-700 text-slate-400 hover:border-slate-500'
            }`}
          >
            {addons[k] ? '✓ ' : '+ '}{label}
          </button>
        ))}
      </div>
      <div data-testid="quote-estimator-result" className="mt-auto border border-amber-500/25 bg-amber-500/5 rounded-xl px-6 py-5">
        <div className="font-mono-tech text-[10px] tracking-[0.25em] text-slate-500 uppercase mb-1">Instant Estimate Range</div>
        <motion.div key={`${low}-${high}`} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="font-display font-extrabold text-2xl sm:text-3xl text-gradient-gold">
          ${low.toLocaleString()} — ${high.toLocaleString()}
        </motion.div>
      </div>
    </div>
  );
}

export default function Automations() {
  return (
    <section id="automations" className="py-24 sm:py-32">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
        <Reveal>
          <Eyebrow>Workflow Automation</Eyebrow>
          <h2 className="font-display font-bold tracking-tight text-2xl sm:text-3xl lg:text-4xl max-w-2xl">
            Automations that hand you back <span className="text-gradient-gold">hours every day.</span>
          </h2>
        </Reveal>

        <div data-testid="automation-bento-grid" className="mt-14 grid lg:grid-cols-12 gap-6">
          <Reveal className="lg:col-span-7" i={1}>
            <div className="h-full bg-[#0F1C33]/60 border border-slate-700/40 hover:border-amber-500/30 rounded-2xl p-8 transition-colors duration-500">
              <div className="flex items-center gap-3 mb-6">
                <Calculator size={22} className="text-amber-400" />
                <h3 className="font-display font-semibold text-xl">Smart Quote Estimators</h3>
              </div>
              <p className="text-sm text-slate-400 mb-6">
                Clients get instant pricing ranges from their own inputs — pre-qualified before you ever pick up the phone. Try the demo:
              </p>
              <QuoteEstimator />
            </div>
          </Reveal>

          <Reveal className="lg:col-span-5" i={2}>
            <div className="h-full bg-[#0F1C33]/60 border border-slate-700/40 hover:border-amber-500/30 rounded-2xl p-8 transition-colors duration-500 flex flex-col">
              <div className="flex items-center gap-3 mb-6">
                <CalendarCheck size={22} className="text-sky-400" />
                <h3 className="font-display font-semibold text-xl">Real-Time Calendar Booking</h3>
              </div>
              <p className="text-sm text-slate-400 mb-6">
                Customers book consultations straight into your live availability. No phone tag, ever.
              </p>
              <div className="mt-auto space-y-2">
                {[['Tue 10:30', 'Consultation — booked', 'text-amber-300'], ['Wed 14:00', 'Quote review — booked', 'text-sky-300'], ['Fri 09:15', 'Open slot', 'text-slate-500']].map(([t, s, c]) => (
                  <div key={t} className="flex items-center justify-between border border-slate-800 rounded-lg px-4 py-3 bg-[#0B1528]/60">
                    <span className="font-mono-tech text-xs text-slate-300">{t}</span>
                    <span className={`font-mono-tech text-[11px] ${c}`}>{s}</span>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>

          <Reveal className="lg:col-span-5" i={3}>
            <div className="h-full bg-[#0F1C33]/60 border border-slate-700/40 hover:border-amber-500/30 rounded-2xl p-8 transition-colors duration-500 flex flex-col">
              <div className="flex items-center gap-3 mb-6">
                <BellRing size={22} className="text-red-400" />
                <h3 className="font-display font-semibold text-xl">Instant Mobile Notifications</h3>
              </div>
              <p className="text-sm text-slate-400 mb-6">
                The second a high-intent lead submits, full project details hit your phone by SMS or email.
              </p>
              <div className="mt-auto border border-slate-800 rounded-xl bg-[#0B1528]/80 px-5 py-4">
                <div className="font-mono-tech text-[10px] tracking-widest text-slate-500 mb-2">SMS · NOW</div>
                <p className="text-xs text-slate-300 leading-relaxed">
                  NEW LEAD — "Kitchen remodel, 12-page site + booking." Budget $4–6k. Reply within 5 min to win 78% more jobs.
                </p>
              </div>
            </div>
          </Reveal>

          <Reveal className="lg:col-span-7" i={4}>
            <div className="h-full relative overflow-hidden rounded-2xl border border-slate-700/40 group min-h-[280px]">
              <img src={ASSETS.cloud} alt="Edge cloud infrastructure" className="absolute inset-0 h-full w-full object-cover opacity-40 transition-transform duration-700 group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-r from-[#050B14] via-[#050B14]/80 to-transparent" />
              <div className="relative p-8 flex flex-col h-full">
                <div className="flex items-center gap-3 mb-4">
                  <Gauge size={22} className="text-amber-400" />
                  <h3 className="font-display font-semibold text-xl">AWS & Cloudflare Edge Infrastructure</h3>
                </div>
                <p className="text-sm text-slate-400 max-w-sm">
                  Global caching, automated SSL and enterprise cloud hosting — the same stack we run for telecom-scale systems.
                </p>
                <div className="mt-auto flex gap-8 pt-8">
                  {[['99.99%', 'UPTIME'], ['<50ms', 'EDGE LATENCY'], ['A+', 'SSL GRADE']].map(([v, l]) => (
                    <div key={l}>
                      <div className="font-mono-tech text-lg text-amber-300 font-semibold">{v}</div>
                      <div className="font-mono-tech text-[10px] tracking-[0.2em] text-slate-500">{l}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
